import React from "react"

type Item = { code: string; title: string; roles: string[]; id: number }
type Props = { items: Item[] }

export function ScrollableDoubleTimeline({ items }: Props) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const railRef = React.useRef<HTMLDivElement>(null)
  const markerRefs = React.useRef<(HTMLDivElement | null)[]>([])
  const [rail, setRail] = React.useState<{
    left: number
    width: number
    top: number
  }>({
    left: 0,
    width: 0,
    top: 0,
  })

  React.useLayoutEffect(() => {
    const recalc = () => {
      if (!containerRef.current || markerRefs.current.length === 0) return
      const first = markerRefs.current[0]
      const last = markerRefs.current[markerRefs.current.length - 1]
      if (!first || !last) return

      const cbox = containerRef.current.getBoundingClientRect()
      const fbox = first.getBoundingClientRect()
      const lbox = last.getBoundingClientRect()

      const fCenterX = fbox.left - cbox.left + fbox.width / 2
      const lCenterX = lbox.left - cbox.left + lbox.width / 2
      const top = fbox.top - cbox.top + fbox.height / 2

      setRail({
        left: fCenterX,
        width: Math.max(0, lCenterX - fCenterX),
        top,
      })
    }

    recalc()
    const ro = new ResizeObserver(recalc)
    ro.observe(document.documentElement)
    window.addEventListener("scroll", recalc, true)
    return () => {
      ro.disconnect()
      window.removeEventListener("scroll", recalc, true)
    }
  }, [items.length])

  return (
    <div className='tw-w-full tw-overflow-x-auto tw-py-6'>
      <div
        ref={containerRef}
        className='tw-relative tw-inline-flex tw-items-start tw-justify-left tw-gap-x-16 tw-px-6'
        style={{ minWidth: "100%" }}
      >
        <div
          ref={railRef}
          className='tw-absolute tw-z-0 tw-pointer-events-none tw-border-t-4 tw-border-dotted tw-border-gray-300'
          style={{ left: rail.left, width: rail.width, top: rail.top }}
        />

        {items.map((it) => (
          <div
            key={it.id}
            className='tw-relative tw-flex tw-flex-col tw-items-center tw-min-w-[240px]'
          >
            <div
              ref={(el) => {
                markerRefs.current[it.id] = el
              }}
              className='tw-bg-[#1F2A44] tw-rounded-full tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-text-white tw-font-medium tw-z-10'
            >
              {it.code}
            </div>

            <h3
              className='tw-font-medium tw-text-gray-900 tw-text-sm tw-uppercase tw-text-center tw-h-10 tw-flex tw-items-end tw-justify-center tw-leading-5 tw-mt-2'
              title={it.title}
            >
              {it.title}
            </h3>

            <div className='tw-relative tw-pl-4 tw-mt-2'>
              {it.roles.length > 1 && (
                <div className='tw-absolute tw-left-3.75 tw-top-4 tw-bottom-4 tw-border-l-2 tw-border-dotted tw-border-gray-400' />
              )}
              <div className='tw-flex tw-flex-col tw-gap-y-4'>
                {it.roles.map((role, rIdx) => {
                  const id = `role-${it.id}-${rIdx}`
                  return (
                    <div
                      key={id}
                      className='tw-flex tw-items-center tw-gap-2'
                    >
                      <span className='tw-w-3 tw-h-3 tw-border tw-border-gray-400 tw-rounded-full tw-bg-white -tw-translate-x-1.5' />
                      <span className='tw-text-sm tw-text-gray-700'>
                        {role}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
